import React, { createContext, useCallback, useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  addToast: (toast: Omit<ToastItem, 'id'>) => void;
  removeToast: (id: string) => void;
}

/* ─── Styling ───────────────────────────────────────────────── */
const typeStyles: Record<ToastType, { container: string; icon: React.ReactNode }> = {
  success: {
    container: 'border-green-200 dark:border-green-800',
    icon: <CheckCircle className="h-5 w-5 text-green-500" />,
  },
  error: {
    container: 'border-red-200 dark:border-red-800',
    icon: <AlertCircle className="h-5 w-5 text-red-500" />,
  },
  warning: {
    container: 'border-amber-200 dark:border-amber-800',
    icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
  },
  info: {
    container: 'border-sky-200 dark:border-sky-800',
    icon: <Info className="h-5 w-5 text-sky-500" />,
  },
};

/* ─── Context ───────────────────────────────────────────────── */
const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

/* ─── Single Toast ──────────────────────────────────────────── */
const ToastCard = React.forwardRef<HTMLDivElement, { toast: ToastItem; onRemove: (id: string) => void }>(function ToastCard({ toast, onRemove }, ref) {
  const { t } = useTranslation();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const style = typeStyles[toast.type];
  const urgent = toast.type === 'error' || toast.type === 'warning';

  const pausedRef = useRef(false);
  const remainingRef = useRef(toast.duration ?? 8000);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const duration = toast.duration ?? 8000;
    remainingRef.current = duration;
    startRef.current = Date.now();
    if (duration > 0) {
      timerRef.current = setTimeout(() => onRemove(toast.id), duration);
    }
    return () => clearTimeout(timerRef.current);
  }, [toast, onRemove]);

  const handleMouseEnter = () => {
    pausedRef.current = true;
    clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startRef.current;
  };

  const handleMouseLeave = () => {
    pausedRef.current = false;
    startRef.current = Date.now();
    if (remainingRef.current > 0) {
      timerRef.current = setTimeout(() => onRemove(toast.id), remainingRef.current);
    }
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      role="alert"
      aria-live="assertive"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-white p-4 shadow-lg',
        'dark:bg-gray-900',
        style.container
      )}
    >
      <span className="shrink-0 mt-0.5" aria-hidden="true">{style.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {toast.title}
        </p>
        {toast.description && (
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {toast.description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className={cn(
          'shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-600',
          'dark:hover:text-gray-300',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
        )}
        aria-label={t('a11y.dismissNotification', 'Dismiss notification')}
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
});

/* ─── Provider ──────────────────────────────────────────────── */
let globalAddToast: ToastContextValue['addToast'] | null = null;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  let counter = useRef(0);

  const addToast = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = `toast-${++counter.current}-${Date.now()}`;
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    globalAddToast = addToast;
    return () => {
      globalAddToast = null;
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {createPortal(
        <div
          role="region"
          aria-label={t('a11y.notificationsRegion', 'Notifications')}
          className="pointer-events-none fixed end-4 top-4 z-[9999] flex flex-col gap-2"
          data-hide-for-bug-report-capture
        >
          <AnimatePresence mode="popLayout">
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onRemove={removeToast} />
            ))}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

/* ─── Imperative API ────────────────────────────────────────── */
function dispatchToast(type: ToastType, title: string, opts?: { description?: string; duration?: number }) {
  if (!globalAddToast) {
    console.warn('[Toast] ToastProvider is not mounted.');
    return;
  }
  globalAddToast({ type, title, ...opts });
}

export const toast = {
  success: (title: string, opts?: { description?: string; duration?: number }) =>
    dispatchToast('success', title, opts),
  error: (title: string, opts?: { description?: string; duration?: number }) =>
    dispatchToast('error', title, opts),
  warning: (title: string, opts?: { description?: string; duration?: number }) =>
    dispatchToast('warning', title, opts),
  info: (title: string, opts?: { description?: string; duration?: number }) =>
    dispatchToast('info', title, opts),
};

ToastProvider.displayName = 'ToastProvider';
